/**
 * FibCompactTable - Data-Dense Horizontal Fibonacci Levels
 *
 * Displays Fibonacci levels in compact horizontal pivot-table format.
 * Shows all levels in one row with prices and distances.
 * Maximum data density with minimal vertical space.
 *
 * PHASE 3.3: Uses QAI Design Tokens for institutional-grade typography
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import {
    QAI_TYPE_SCALE,
    QAI_FONTS,
    QAI_TRACKING,
    QAI_LAYOUT,
    QAI_COLORS
} from '../../../theme/qaiDesignTokens';

/**
 * Get level color based on position relative to price - using design tokens
 */
const getLevelColor = (status, isNearest) => {
    if (isNearest) return QAI_COLORS.semantic.neutral;
    if (!status) return QAI_COLORS.text.muted;
    const s = status.toUpperCase();
    if (s === 'ABOVE') return QAI_COLORS.semantic.bullish;
    if (s === 'BELOW') return QAI_COLORS.semantic.bearish;
    if (s === 'AT') return QAI_COLORS.semantic.neutral;
    return QAI_COLORS.text.secondary;
};

/**
 * Get distance color - using design tokens
 */
const getDistanceColor = (distance) => {
    if (distance === null || distance === undefined) return QAI_COLORS.text.muted;
    const d = Math.abs(distance);
    if (d <= 5) return QAI_COLORS.semantic.neutral;  // Very close
    if (d <= 15) return QAI_COLORS.semantic.bullish; // Close
    return QAI_COLORS.text.secondary;                 // Far
};

/**
 * Format price for display - auto-detect precision based on value
 */
const formatPrice = (price) => {
    if (!price && price !== 0) return '--';
    // Auto-detect decimals: forex (5), indices (2), large numbers (0)
    if (price > 1000) return price.toFixed(2);      // Indices like XAUUSD, US30
    if (price > 10) return price.toFixed(4);         // Pairs like USDJPY
    return price.toFixed(5);                          // Standard forex pairs
};

/**
 * FibCompactTable Component
 */
const FibCompactTable = memo(({ data, currentPrice }) => {
    const levels = data?.levels || [];
    const swingHigh = data?.swingHigh;
    const swingLow = data?.swingLow;
    const nearestLevel = data?.nearestLevel;
    const distanceToNearest = data?.distanceToNearest;

    // Standard Fibonacci levels to display
    const standardLevels = ['0%', '23.6%', '38.2%', '50%', '61.8%', '78.6%', '100%'];

    // Normalize level key (handles both "0%" and "0.0%" formats from different endpoints)
    const normalizeLevel = (level) => {
        if (!level) return '';
        // Remove spaces, convert "0.0%" to "0%", "50.0%" to "50%", "100.0%" to "100%"
        return level.replace(' ', '').replace('.0%', '%');
    };

    // Map levels to standard positions
    const levelMap = {};
    levels.forEach(l => {
        const key = normalizeLevel(l.level);
        levelMap[key] = l;
    });

    // Calculate distance from current price
    const getDistance = (levelPrice) => {
        if (!levelPrice || !currentPrice) return null;
        return ((currentPrice - levelPrice) / currentPrice * 10000).toFixed(1); // in pips (approx)
    };

    return (
        <Box sx={{
            backgroundColor: QAI_COLORS.background.primary,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${QAI_COLORS.border.subtle}`
        }}>
            {/* Header Row - PHASE 3.3: Compact 32px */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: QAI_LAYOUT.rowCompact,  // 32px
                    backgroundColor: 'rgba(168, 85, 247, 0.08)',
                    borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
                    padding: QAI_LAYOUT.paddingCompact
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                        color: QAI_COLORS.text.muted,
                        fontFamily: QAI_FONTS.mono,
                        letterSpacing: QAI_TRACKING.wide
                    }}>
                        SWING:
                    </Typography>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE.xs,  // 11px
                        color: QAI_COLORS.semantic.bullish,
                        fontFamily: QAI_FONTS.mono,
                        fontWeight: 600
                    }}>
                        H {formatPrice(swingHigh)}
                    </Typography>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE.xs,  // 11px
                        color: QAI_COLORS.semantic.bearish,
                        fontFamily: QAI_FONTS.mono,
                        fontWeight: 600
                    }}>
                        L {formatPrice(swingLow)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                        color: QAI_COLORS.text.muted,
                        fontFamily: QAI_FONTS.mono,
                        letterSpacing: QAI_TRACKING.wide
                    }}>
                        NEAREST:
                    </Typography>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE.xs,  // 11px
                        color: QAI_COLORS.semantic.neutral,
                        fontFamily: QAI_FONTS.mono,
                        fontWeight: 700
                    }}>
                        {nearestLevel || '--'}
                    </Typography>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                        color: QAI_COLORS.semantic.neutral,
                        fontFamily: QAI_FONTS.mono
                    }}>
                        ({distanceToNearest ? `${distanceToNearest.toFixed(1)} pips` : '--'})
                    </Typography>
                </Box>
            </Box>

            {/* Levels Header Row - PHASE 3.3: 11px headers */}
            <Box
                sx={{
                    display: 'flex',
                    borderBottom: `1px solid ${QAI_COLORS.border.subtle}`
                }}
            >
                {standardLevels.map((level, index) => {
                    const levelData = levelMap[level];
                    const isNearest = normalizeLevel(nearestLevel) === level;

                    return (
                        <Box
                            key={level}
                            sx={{
                                flex: 1,
                                padding: QAI_LAYOUT.paddingCell,
                                textAlign: 'center',
                                borderRight: index < standardLevels.length - 1 ? `1px solid ${QAI_COLORS.border.subtle}` : 'none',
                                backgroundColor: isNearest ? QAI_COLORS.semantic.neutralMuted : 'transparent'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: QAI_TYPE_SCALE.xs,  // 11px
                                    fontWeight: 600,
                                    color: isNearest ? QAI_COLORS.semantic.neutral : QAI_COLORS.text.muted,
                                    fontFamily: QAI_FONTS.mono,
                                    letterSpacing: QAI_TRACKING.wide
                                }}
                            >
                                {level}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Price Row - PHASE 3.3: 13px prices */}
            <Box
                sx={{
                    display: 'flex',
                    borderBottom: `1px solid ${QAI_COLORS.border.subtle}`
                }}
            >
                {standardLevels.map((level, index) => {
                    const levelData = levelMap[level];
                    const isNearest = normalizeLevel(nearestLevel) === level;
                    const price = levelData?.price;

                    return (
                        <Box
                            key={`price-${level}`}
                            sx={{
                                flex: 1,
                                padding: QAI_LAYOUT.paddingCell,
                                textAlign: 'center',
                                borderRight: index < standardLevels.length - 1 ? `1px solid ${QAI_COLORS.border.subtle}` : 'none',
                                backgroundColor: isNearest ? QAI_COLORS.semantic.neutralMuted : 'transparent'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: QAI_TYPE_SCALE.sm,  // 13px - readable
                                    fontWeight: 600,
                                    color: getLevelColor(levelData?.status, isNearest),
                                    fontFamily: QAI_FONTS.mono,
                                    letterSpacing: QAI_TRACKING.tight
                                }}
                            >
                                {formatPrice(price)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Distance Row - PHASE 3.3: 10px micro labels */}
            <Box sx={{ display: 'flex' }}>
                {standardLevels.map((level, index) => {
                    const levelData = levelMap[level];
                    const isNearest = normalizeLevel(nearestLevel) === level;
                    const distance = getDistance(levelData?.price);
                    const status = levelData?.status?.toUpperCase();
                    const arrow = status === 'ABOVE' ? '▲' : status === 'BELOW' ? '▼' : '●';

                    return (
                        <Box
                            key={`dist-${level}`}
                            sx={{
                                flex: 1,
                                padding: QAI_LAYOUT.paddingCell,
                                textAlign: 'center',
                                borderRight: index < standardLevels.length - 1 ? `1px solid ${QAI_COLORS.border.subtle}` : 'none',
                                backgroundColor: isNearest ? QAI_COLORS.semantic.neutralMuted : 'transparent'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                                    fontWeight: 500,
                                    color: getDistanceColor(distance),
                                    fontFamily: QAI_FONTS.mono
                                }}
                            >
                                {arrow} {distance !== null ? `${distance > 0 ? '+' : ''}${distance}` : '--'}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Current Price Indicator - PHASE 3.3 */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: QAI_COLORS.background.surface2,
                    borderTop: `1px solid ${QAI_COLORS.border.default}`,
                    padding: QAI_LAYOUT.paddingCell,
                    gap: 1
                }}
            >
                <Typography sx={{
                    fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                    color: QAI_COLORS.text.muted,
                    fontFamily: QAI_FONTS.mono,
                    letterSpacing: QAI_TRACKING.wide
                }}>
                    PRICE:
                </Typography>
                <Typography sx={{
                    fontSize: QAI_TYPE_SCALE.sm,  // 13px
                    color: QAI_COLORS.text.primary,
                    fontFamily: QAI_FONTS.mono,
                    fontWeight: 700,
                    letterSpacing: QAI_TRACKING.tight
                }}>
                    {formatPrice(currentPrice)}
                </Typography>
                <Typography sx={{
                    fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                    color: QAI_COLORS.text.muted,
                    fontFamily: QAI_FONTS.mono
                }}>
                    • Distance in pips from current
                </Typography>
            </Box>
        </Box>
    );
});

FibCompactTable.displayName = 'FibCompactTable';

export default FibCompactTable;
